import React, { createContext, useContext, useEffect, useState } from "react";
import { api } from "@/lib/api";

/**
 * Global site-settings store.
 *
 * Before this existed each top-level page (`ServicesPage`,
 * `SoftwarePage`, `LaptopsPage`, `AboutPage`, …) owned its own
 * `useState({}) + useEffect(fetch /settings)` pair. On every
 * route change React unmounted the previous page and mounted the
 * next one with `s = {}`; the JSX `s.foo || "hard-coded fallback"`
 * pattern therefore rendered the stale fallback string during the
 * ~1 network RTT to `/api/settings`, then re-rendered with the
 * live CMS copy. To the user this looked like a flash of an "old
 * hero" on every navigation between top-level tabs.
 *
 * The fix is a single provider mounted at the app root that:
 *
 *   1. Reads a locally-cached copy of `/api/settings` from
 *      `localStorage` synchronously as its `useState` initializer,
 *      so the very first render after any route change already has
 *      the last-known CMS data (no fallback flash for returning
 *      visitors — which is the whole SPA navigation case).
 *   2. Fetches `/api/settings` once, updates the cache in
 *      `localStorage`, and shares the resulting object with every
 *      consumer via the `useSettings()` hook.
 *
 * We deliberately do NOT block first paint on the fetch, block
 * routes on a loading state, or introduce a skeleton — those all
 * violate the acceptance criteria. The `localStorage` cache does
 * the equivalent of a fast synchronous read; the network fetch
 * then reconciles quietly.
 */

const CACHE_KEY = "ur_settings_cache_v2";

const readCache = () => {
  try {
    if (typeof window === "undefined") return {};
    const raw = window.localStorage.getItem(CACHE_KEY);
    if (!raw) return {};
    const parsed = JSON.parse(raw);
    if (parsed && typeof parsed === "object") return parsed;
    return {};
  } catch (_) {
    return {};
  }
};

const writeCache = (data) => {
  try {
    if (typeof window === "undefined") return;
    window.localStorage.setItem(CACHE_KEY, JSON.stringify(data));
  } catch (_) {
    /* quota / private-mode / disabled — silently ignore, we still
       have the in-memory value */
  }
};

const SettingsContext = createContext({ settings: {}, refresh: () => {} });

export const SettingsProvider = ({ children }) => {
  // Lazy initializer runs exactly once on first mount and reads the
  // last-known /settings snapshot from localStorage. Returning
  // visitors therefore render every page with the correct hero copy
  // from the very first paint. Brand-new visitors start with `{}`
  // and each page falls through to the inline `||` fallback until
  // the fetch below resolves (~200 ms) — which matches the
  // pre-existing behavior for that specific first-cold-visit case.
  const [settings, setSettings] = useState(() => readCache());

  useEffect(() => {
    let cancelled = false;
    api
      .get("/settings")
      .then((r) => {
        if (cancelled) return;
        setSettings(r.data || {});
        writeCache(r.data || {});
      })
      .catch(() => {});
    return () => {
      cancelled = true;
    };
  }, []);

  const refresh = React.useCallback(() => {
    api.get("/settings").then((r) => {
      setSettings(r.data || {});
      writeCache(r.data || {});
    }).catch(() => {});
  }, []);

  return (
    <SettingsContext.Provider value={{ settings, refresh }}>
      {children}
    </SettingsContext.Provider>
  );
};

/**
 * Read the site-wide CMS settings object.
 *
 *   const s = useSettings();
 *   const eyebrow = s.services_eyebrow || "Our Services";
 *
 * Every consumer gets the SAME `s` reference — route changes
 * never remount the store and never trigger a re-fetch, so the
 * fallback-flash bug cannot recur.
 */
export const useSettings = () => useContext(SettingsContext).settings;

/** Force a re-fetch of `/api/settings` — used by admin flows that
 *  save new CMS copy and want the public site to update without a
 *  hard reload. */
export const useRefreshSettings = () => useContext(SettingsContext).refresh;
